import {ArgumentType, BlockType} from 'scratch-vm';
import formatMessage from 'format-message';

import connection from './connection.js';

/**
 * Rozšírenie „Reachy Mini" — bloky, ktorými deti ovládajú skutočného robota.
 *
 * Každý blok pošle jeden príkaz lokálnemu bridge-u a počká, kým robot pohyb
 * dokončí (plán §11.1). Senzorové bloky čítajú zrkadlo v connection.js, takže
 * odpovedajú okamžite a Scratch pri nich neseká (plán §3.2).
 *
 * Názvy príkazov kopírujú `reachy_kids`, aby prechod na Python kurz nebol
 * nový jazyk, len nový zápis (plán §11.5).
 */

const reachySvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" rx="8" fill="#0FBD8C"/><circle cx="14" cy="19" r="3.2" fill="white"/><circle cx="26" cy="19" r="3.2" fill="white"/><rect x="8" y="10" width="24" height="19" rx="6" fill="none" stroke="white" stroke-width="2.2"/><path d="M12 10V6M28 10V6" stroke="white" stroke-width="2.2" stroke-linecap="round"/></svg>';
const menuIconURI = `data:image/svg+xml;base64,${typeof btoa === 'undefined' ? '' : btoa(reachySvg)}`;
const blockIconURI = menuIconURI;

/** Ako často sa pozerať na zrkadlo senzorov pri blokoch „čakaj na…". */
const WAIT_POLL_MS = 50;

const msg = (id, def, description) => formatMessage({id: `reachy.${id}`, default: def, description});

const item = (value, id, def) => ({value, text: msg(id, def, `Menu item: ${def}`)});

// Texty menu sú tu po anglicky a slovenčina k nim je v custom-locales/sk.json.
// (Preklady sa načítajú až pri zmene jazyka — bez toho by sa slovenské bloky
// ukázali aj v anglickom rozhraní.)

/** Emócie — 12 jednoduchých názvov z reachy_kids (celkovo ich robot vie 87). */
const EMOTIONS = [
    ['happy', 'happy'], ['sad', 'sad'], ['confused', 'confused'],
    ['surprised', 'surprised'], ['proud', 'proud'], ['scared', 'scared'],
    ['curious', 'curious'], ['excited', 'excited'], ['laughing', 'laughing'],
    ['tired', 'tired'], ['angry', 'angry'], ['bored', 'bored']
];

const COLORS = [
    ['red', 'red'], ['orange', 'orange'], ['yellow', 'yellow'],
    ['green', 'green'], ['blue', 'blue'], ['purple', 'purple'],
    ['pink', 'pink'], ['white', 'white'], ['black', 'black']
];

const SOUNDS = [
    ['hello', 'hello'], ['bye', 'bye'], ['confused', 'confused'],
    ['count', 'count'], ['dance', 'dance'], ['impatient', 'impatient']
];

const LANGUAGES = [
    ['sk', 'Slovak'], ['en', 'English'], ['cs', 'Czech'], ['de', 'German'],
    ['es', 'Spanish'], ['fr', 'French'], ['it', 'Italian'],
    ['pl', 'Polish'], ['hu', 'Hungarian'], ['uk', 'Ukrainian']
];

class Scratch3ReachyBlocks {
    constructor (runtime) {
        this.runtime = runtime;
        this._waiters = new Set();

        connection.start();

        if (this.runtime) {
            // Červené tlačidlo musí zastaviť robota - inak tancuje ďalej,
            // aj keď dieťa program zastavilo (plán §3.2).
            this.runtime.on('PROJECT_STOP_ALL', () => this._stopEverything());
        }

        // Klobúky spúšťame my, keď jav naozaj nastane - viď _spusti().
        connection.onUdalost(nazov => this._spusti(nazov));

        if (typeof window !== 'undefined') {
            // Poistka pri zatvorení karty. Druhou poistkou je watchdog v bridgei.
            window.addEventListener('beforeunload', () => connection.stop());
        }
    }

    /**
     * Spusti klobúkové bloky patriace k danému javu.
     *
     * Robíme to sami, lebo `isEdgeActivated` klobúky sa skúšajú každý snímok
     * a VM pri každom skúšaní založí a zruší vlákno. Aj zrušené vlákno sa
     * počíta ako „program beží", takže zelená vlajka by svietila stále, hoci
     * nikto netlieskal (namerané 2026-08-09).
     * @param {string} nazov - 'tlesknutie', 'tvar' alebo 'antena'
     */
    _spusti (nazov) {
        if (!this.runtime) return;
        const opcodes = {
            tlesknutie: 'reachyBlocks_whenClap',
            tvar: 'reachyBlocks_whenSeesFace',
            antena: 'reachyBlocks_whenPressed'
        };
        const opcode = opcodes[nazov];
        if (opcode) this.runtime.startHats(opcode);
    }

    _stopEverything () {
        connection.stop();
        // Bloky „čakaj na…" sa zrušia, nech skript neostane visieť.
        const waiters = Array.from(this._waiters);
        this._waiters.clear();
        waiters.forEach(cancel => cancel());
    }

    /**
     * Čakaj, kým `podmienka()` neplatí — nad zrkadlom senzorov, nie v bridgei.
     * Blokujúce čakanie v bridgei by uväznilo vykonávacie vlákno (plán §11.6).
     * @param {Function} podmienka - vráti true, keď sa už čakať nemá
     * @returns {Promise} - splní sa po dočkaní alebo po zastavení programu
     */
    _waitFor (podmienka) {
        return new Promise(resolve => {
            let timer = null;
            const hotovo = () => {
                if (timer !== null) clearInterval(timer);
                this._waiters.delete(hotovo);
                resolve();
            };
            timer = setInterval(() => {
                if (podmienka()) hotovo();
            }, WAIT_POLL_MS);
            this._waiters.add(hotovo);
        });
    }

    getInfo () {
        return {
            id: 'reachyBlocks',
            name: msg('categoryName', 'Reachy Mini', 'Name of the Reachy Mini extension'),
            menuIconURI: menuIconURI,
            blockIconURI: blockIconURI,
            color1: '#0FBD8C',
            color2: '#0DA57A',
            color3: '#0B8E69',
            blocks: [
                // -- hlava ---------------------------------------------------
                {
                    opcode: 'look',
                    blockType: BlockType.COMMAND,
                    text: msg('look', 'look [SMER] by [KOLKO]', 'Look in a direction'),
                    arguments: {
                        SMER: {type: ArgumentType.STRING, menu: 'SMER', defaultValue: 'up'},
                        KOLKO: {type: ArgumentType.NUMBER, defaultValue: 25}
                    }
                },
                {
                    opcode: 'lookForward',
                    blockType: BlockType.COMMAND,
                    text: msg('lookForward', 'look forward', 'Look forward')
                },
                {
                    opcode: 'tilt',
                    blockType: BlockType.COMMAND,
                    text: msg('tilt', 'tilt head [STRANA] by [KOLKO]', 'Tilt head'),
                    arguments: {
                        STRANA: {type: ArgumentType.STRING, menu: 'STRANA', defaultValue: 'left'},
                        KOLKO: {type: ArgumentType.NUMBER, defaultValue: 20}
                    }
                },
                {
                    opcode: 'nod',
                    blockType: BlockType.COMMAND,
                    text: msg('nod', 'nod [KRAT] times', 'Nod yes'),
                    arguments: {KRAT: {type: ArgumentType.NUMBER, defaultValue: 2}}
                },
                {
                    opcode: 'shake',
                    blockType: BlockType.COMMAND,
                    text: msg('shake', 'shake head [KRAT] times', 'Shake head no'),
                    arguments: {KRAT: {type: ArgumentType.NUMBER, defaultValue: 2}}
                },
                {
                    opcode: 'lift',
                    blockType: BlockType.COMMAND,
                    text: msg('lift', 'lift neck by [KOLKO] mm', 'Lift the neck'),
                    arguments: {KOLKO: {type: ArgumentType.NUMBER, defaultValue: 10}}
                },
                {
                    opcode: 'headCenter',
                    blockType: BlockType.COMMAND,
                    text: msg('headCenter', 'center head', 'Center the head')
                },
                {
                    opcode: 'lookAt',
                    blockType: BlockType.COMMAND,
                    text: msg('lookAt', 'look at point x [X] y [Y]', 'Look at a point in the camera image'),
                    arguments: {
                        X: {type: ArgumentType.NUMBER, defaultValue: 0},
                        Y: {type: ArgumentType.NUMBER, defaultValue: 0}
                    }
                },
                {
                    opcode: 'followFace',
                    blockType: BlockType.COMMAND,
                    text: msg('followFace', 'follow face [STAV]', 'Turn face following on or off'),
                    arguments: {STAV: {type: ArgumentType.STRING, menu: 'STAV', defaultValue: 'on'}}
                },
                {
                    opcode: 'headPosition',
                    blockType: BlockType.REPORTER,
                    text: msg('headPosition', 'head position [OS]', 'Head position reporter'),
                    arguments: {OS: {type: ArgumentType.STRING, menu: 'OS', defaultValue: 'up'}}
                },

                '---',
                // -- telo ----------------------------------------------------
                {
                    opcode: 'turnBody',
                    blockType: BlockType.COMMAND,
                    text: msg('turnBody', 'turn body [STRANA] by [KOLKO]', 'Turn the body'),
                    arguments: {
                        STRANA: {type: ArgumentType.STRING, menu: 'STRANA', defaultValue: 'left'},
                        KOLKO: {type: ArgumentType.NUMBER, defaultValue: 60}
                    }
                },
                {
                    opcode: 'turnBodyTo',
                    blockType: BlockType.COMMAND,
                    text: msg('turnBodyTo', 'turn body to angle [UHOL]', 'Turn the body to an angle'),
                    arguments: {UHOL: {type: ArgumentType.NUMBER, defaultValue: 0}}
                },
                {
                    opcode: 'spin',
                    blockType: BlockType.COMMAND,
                    text: msg('spin', 'spin [KRAT] times', 'Spin around'),
                    arguments: {KRAT: {type: ArgumentType.NUMBER, defaultValue: 1}}
                },
                {
                    opcode: 'bodyCenter',
                    blockType: BlockType.COMMAND,
                    text: msg('bodyCenter', 'body to front', 'Body back to front')
                },
                {
                    opcode: 'bodyAngle',
                    blockType: BlockType.REPORTER,
                    text: msg('bodyAngle', 'body angle', 'Body angle reporter')
                },

                '---',
                // -- antény --------------------------------------------------
                {
                    opcode: 'antennas',
                    blockType: BlockType.COMMAND,
                    text: msg('antennas', 'antennas [POLOHA]', 'Move both antennas'),
                    arguments: {POLOHA: {type: ArgumentType.STRING, menu: 'POLOHA', defaultValue: 'up'}}
                },
                {
                    opcode: 'wave',
                    blockType: BlockType.COMMAND,
                    text: msg('wave', 'wave [KRAT] times', 'Wave with the antennas'),
                    arguments: {KRAT: {type: ArgumentType.NUMBER, defaultValue: 3}}
                },
                {
                    opcode: 'wiggle',
                    blockType: BlockType.COMMAND,
                    text: msg('wiggle', 'wiggle [KRAT] times', 'Wiggle the antennas'),
                    arguments: {KRAT: {type: ArgumentType.NUMBER, defaultValue: 4}}
                },
                {
                    opcode: 'antennasMove',
                    blockType: BlockType.COMMAND,
                    text: msg('antennasMove', 'set antenna right [VPRAVO] left [VLAVO]', 'Set each antenna'),
                    arguments: {
                        VPRAVO: {type: ArgumentType.NUMBER, defaultValue: 0},
                        VLAVO: {type: ArgumentType.NUMBER, defaultValue: 0}
                    }
                },
                {
                    opcode: 'isPressed',
                    blockType: BlockType.BOOLEAN,
                    text: msg('isPressed', 'antenna [KTORA] pressed?', 'Is an antenna pressed?'),
                    arguments: {KTORA: {type: ArgumentType.STRING, menu: 'KTORA', defaultValue: 'any'}}
                },
                {
                    opcode: 'waitForPress',
                    blockType: BlockType.COMMAND,
                    text: msg('waitForPress', 'wait for antenna press', 'Wait for an antenna press')
                },
                {
                    opcode: 'whenPressed',
                    blockType: BlockType.HAT,
                    // Klobúk spúšťame sami pri stlačení (viď _spusti), nie
                    // skúšaním každý snímok - inak by svietila zelená vlajka.
                    isEdgeActivated: false,
                    text: msg('whenPressed', 'when I press antenna [KTORA]', 'When an antenna is pressed'),
                    arguments: {KTORA: {type: ArgumentType.STRING, menu: 'KTORA', defaultValue: 'any'}}
                },

                '---',
                // -- hlas ----------------------------------------------------
                {
                    opcode: 'say',
                    blockType: BlockType.COMMAND,
                    text: msg('say', 'say [TEXT]', 'Say something out loud'),
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: msg('say.default', 'Hi, my name is Reachy!', 'Default sentence to say')
                        }
                    }
                },
                {
                    opcode: 'sayInLanguage',
                    blockType: BlockType.COMMAND,
                    text: msg('sayInLanguage', 'say [TEXT] in [JAZYK]', 'Say something in a language'),
                    arguments: {
                        TEXT: {type: ArgumentType.STRING, defaultValue: 'Hello!'},
                        JAZYK: {type: ArgumentType.STRING, menu: 'JAZYK', defaultValue: 'en'}
                    }
                },
                {
                    opcode: 'playSound',
                    blockType: BlockType.COMMAND,
                    text: msg('playSound', 'play sound [ZVUK]', 'Play a built-in sound'),
                    arguments: {ZVUK: {type: ArgumentType.STRING, menu: 'ZVUK', defaultValue: 'hello'}}
                },
                {
                    opcode: 'setVolume',
                    blockType: BlockType.COMMAND,
                    text: msg('setVolume', 'set volume to [HLASITOST]', 'Set the speaker volume'),
                    arguments: {HLASITOST: {type: ArgumentType.NUMBER, defaultValue: 60}}
                },

                '---',
                // -- emócie a tanec ------------------------------------------
                {
                    opcode: 'emotion',
                    blockType: BlockType.COMMAND,
                    text: msg('emotion', 'play emotion [EMOCIA]', 'Play a whole-body emotion'),
                    arguments: {EMOCIA: {type: ArgumentType.STRING, menu: 'EMOCIA', defaultValue: 'happy'}}
                },
                {
                    opcode: 'dance',
                    blockType: BlockType.COMMAND,
                    text: msg('dance', 'dance [SEKUND] seconds at tempo [TEMPO]', 'Dance for a number of seconds'),
                    arguments: {
                        SEKUND: {type: ArgumentType.NUMBER, defaultValue: 15},
                        TEMPO: {type: ArgumentType.NUMBER, defaultValue: 100}
                    }
                },
                {
                    opcode: 'danceSong',
                    blockType: BlockType.COMMAND,
                    text: msg('danceSong', 'dance to song [SUBOR]', 'Dance to a song file'),
                    arguments: {SUBOR: {type: ArgumentType.STRING, defaultValue: 'song.mp3'}}
                },

                '---',
                // -- oči -----------------------------------------------------
                {
                    opcode: 'seesFace',
                    blockType: BlockType.BOOLEAN,
                    text: msg('seesFace', 'sees a face?', 'Does the robot see a face?')
                },
                {
                    opcode: 'countFaces',
                    blockType: BlockType.REPORTER,
                    text: msg('countFaces', 'number of faces', 'How many faces the robot sees')
                },
                {
                    opcode: 'facePosition',
                    blockType: BlockType.REPORTER,
                    text: msg('facePosition', 'face [SURADNICA]', 'Position of the biggest face'),
                    arguments: {SURADNICA: {type: ArgumentType.STRING, menu: 'SURADNICA', defaultValue: 'x'}}
                },
                {
                    opcode: 'seesColor',
                    blockType: BlockType.BOOLEAN,
                    text: msg('seesColor', 'sees colour [FARBA]?', 'Does the robot see a colour?'),
                    arguments: {FARBA: {type: ArgumentType.STRING, menu: 'FARBA', defaultValue: 'red'}}
                },
                {
                    opcode: 'colorPosition',
                    blockType: BlockType.REPORTER,
                    text: msg('colorPosition', 'colour [FARBA] [SURADNICA]', 'Position of a colour blob'),
                    arguments: {
                        FARBA: {type: ArgumentType.STRING, menu: 'FARBA', defaultValue: 'red'},
                        SURADNICA: {type: ArgumentType.STRING, menu: 'SURADNICA', defaultValue: 'x'}
                    }
                },
                {
                    opcode: 'brightness',
                    blockType: BlockType.REPORTER,
                    text: msg('brightness', 'brightness', 'How bright it is in front of the robot')
                },
                {
                    opcode: 'isDark',
                    blockType: BlockType.BOOLEAN,
                    text: msg('isDark', 'eyes covered?', 'Are the robot eyes covered?')
                },
                {
                    opcode: 'takePhoto',
                    blockType: BlockType.COMMAND,
                    text: msg('takePhoto', 'take photo [SUBOR]', 'Take a photo'),
                    arguments: {SUBOR: {type: ArgumentType.STRING, defaultValue: 'foto.jpg'}}
                },
                {
                    opcode: 'whenSeesFace',
                    blockType: BlockType.HAT,
                    isEdgeActivated: false,
                    text: msg('whenSeesFace', 'when it sees a face', 'When the robot sees a face')
                },

                '---',
                // -- uši -----------------------------------------------------
                {
                    opcode: 'loudness',
                    blockType: BlockType.REPORTER,
                    text: msg('loudness', 'loudness around', 'How loud it is around the robot')
                },
                {
                    opcode: 'isLoud',
                    blockType: BlockType.BOOLEAN,
                    text: msg('isLoud', 'is it loud?', 'Is it loud?')
                },
                {
                    opcode: 'hearsClap',
                    blockType: BlockType.BOOLEAN,
                    text: msg('hearsClap', 'hears a clap?', 'Did the robot hear a clap?')
                },
                {
                    opcode: 'waitForClap',
                    blockType: BlockType.COMMAND,
                    text: msg('waitForClap', 'wait for a clap', 'Wait for a clap')
                },
                {
                    opcode: 'listen',
                    blockType: BlockType.REPORTER,
                    text: msg('listen', 'listen and recognise speech', 'Listen and recognise speech')
                },
                {
                    opcode: 'record',
                    blockType: BlockType.COMMAND,
                    text: msg('record', 'record sound [SUBOR] for [SEKUND] seconds', 'Record a sound file'),
                    arguments: {
                        SUBOR: {type: ArgumentType.STRING, defaultValue: 'nahravka.wav'},
                        SEKUND: {type: ArgumentType.NUMBER, defaultValue: 5}
                    }
                },
                {
                    opcode: 'whenClap',
                    blockType: BlockType.HAT,
                    isEdgeActivated: false,
                    text: msg('whenClap', 'when I clap', 'When I clap')
                },

                '---',
                // -- základ --------------------------------------------------
                {
                    opcode: 'isRobotConnected',
                    blockType: BlockType.BOOLEAN,
                    text: msg('isRobotConnected', 'robot connected?', 'Is the robot connected?')
                },
                {
                    opcode: 'reset',
                    blockType: BlockType.COMMAND,
                    text: msg('reset', 'everything back to start position', 'Reset the robot pose')
                },
                {
                    opcode: 'sleep',
                    blockType: BlockType.COMMAND,
                    text: msg('sleep', 'put robot to sleep', 'Put the robot to sleep')
                }
            ],
            menus: {
                SMER: {
                    acceptReporters: true,
                    items: [
                        item('up', 'smer.up', 'up'), item('down', 'smer.down', 'down'),
                        item('left', 'smer.left', 'left'), item('right', 'smer.right', 'right')
                    ]
                },
                STRANA: {
                    acceptReporters: true,
                    items: [item('left', 'strana.left', 'left'), item('right', 'strana.right', 'right')]
                },
                STAV: {
                    acceptReporters: true,
                    items: [item('on', 'stav.on', 'on'), item('off', 'stav.off', 'off')]
                },
                OS: {
                    acceptReporters: true,
                    items: [
                        item('up', 'os.up', 'up'), item('turn', 'os.turn', 'turn'),
                        item('tilt', 'os.tilt', 'tilt'), item('lift', 'os.lift', 'lift')
                    ]
                },
                POLOHA: {
                    acceptReporters: true,
                    items: [
                        item('up', 'poloha.up', 'up'), item('down', 'poloha.down', 'down'),
                        item('center', 'poloha.center', 'to centre')
                    ]
                },
                KTORA: {
                    acceptReporters: true,
                    items: [
                        item('any', 'ktora.any', 'any'),
                        item('left', 'ktora.left', 'left'), item('right', 'ktora.right', 'right')
                    ]
                },
                SURADNICA: {
                    acceptReporters: true,
                    items: [
                        item('x', 'suradnica.x', 'x'), item('y', 'suradnica.y', 'y'),
                        item('size', 'suradnica.size', 'size')
                    ]
                },
                EMOCIA: {
                    acceptReporters: true,
                    items: EMOTIONS.map(([value, text]) => item(value, `emocia.${value}`, text))
                },
                FARBA: {
                    acceptReporters: true,
                    items: COLORS.map(([value, text]) => item(value, `farba.${value}`, text))
                },
                ZVUK: {
                    acceptReporters: true,
                    items: SOUNDS.map(([value, text]) => item(value, `zvuk.${value}`, text))
                },
                JAZYK: {
                    acceptReporters: true,
                    items: LANGUAGES.map(([value, text]) => item(value, `jazyk.${value}`, text))
                }
            }
        };
    }

    // -- pomocné -------------------------------------------------------------

    _num (hodnota, predvolene) {
        const cislo = Number(hodnota);
        return isNaN(cislo) ? predvolene : cislo;
    }

    /**
     * Pošli príkaz robotovi a počkaj, kým ho dokončí.
     *
     * Vráti Promise **bez hodnoty**: keď dieťa klikne na blok v ploche,
     * Scratch by inak ukázal bublinu s odpoveďou bridge-u („[object Object]").
     * @param {string} cmd - bodková cesta príkazu
     * @param {object} [args] - argumenty príkazu
     * @returns {Promise} - splní sa po dokončení pohybu
     */
    _prikaz (cmd, args) {
        return connection.send(cmd, args).then(() => {});
    }

    _sensors () {
        return connection.sensors;
    }

    // -- hlava ---------------------------------------------------------------

    // Blok hovorí „o toľko", takže posiela prírastok, nie cieľovú polohu.
    // Inak sa hlava po prvom stlačení šípky už nepohla — druhé stlačenie
    // žiadalo tú istú polohu, v ktorej už bola.
    look (args) {
        const smery = {
            up: {up: 1, left: 0},
            down: {up: -1, left: 0},
            left: {up: 0, left: 1},
            right: {up: 0, left: -1}
        };
        const smer = smery[args.SMER] || smery.up;
        const kolko = this._num(args.KOLKO, 25);
        return this._prikaz('head.look_by', {
            up: smer.up * kolko,
            left: smer.left * kolko
        });
    }

    lookForward () {
        return this._prikaz('head.look_forward');
    }

    tilt (args) {
        const kolko = this._num(args.KOLKO, 20);
        return this._prikaz('head.tilt_by', {
            left: args.STRANA === 'right' ? -kolko : kolko
        });
    }

    nod (args) {
        return this._prikaz('head.nod', {times: this._num(args.KRAT, 2)});
    }

    shake (args) {
        return this._prikaz('head.shake', {times: this._num(args.KRAT, 2)});
    }

    lift (args) {
        return this._prikaz('head.lift_by', {amount: this._num(args.KOLKO, 10)});
    }

    headCenter () {
        return this._prikaz('head.center');
    }

    lookAt (args) {
        return this._prikaz('head.look_at', {
            x: this._num(args.X, 0),
            y: this._num(args.Y, 0)
        });
    }

    followFace (args) {
        return this._prikaz('head.follow_face', {on: args.STAV !== 'off'});
    }

    headPosition (args) {
        const hlava = this._sensors().hlava || {};
        return hlava[args.OS] || 0;
    }

    // -- telo ----------------------------------------------------------------

    turnBody (args) {
        const kolko = this._num(args.KOLKO, 60);
        return this._prikaz('body.turn_by', {
            left: args.STRANA === 'right' ? -kolko : kolko
        });
    }

    turnBodyTo (args) {
        return this._prikaz('body.turn_to', {angle: this._num(args.UHOL, 0)});
    }

    spin (args) {
        return this._prikaz('body.spin', {times: this._num(args.KRAT, 1)});
    }

    bodyCenter () {
        return this._prikaz('body.center');
    }

    bodyAngle () {
        return this._sensors().telo || 0;
    }

    // -- antény --------------------------------------------------------------

    antennas (args) {
        const prikazy = {up: 'antennas.up', down: 'antennas.down', center: 'antennas.center'};
        return this._prikaz(prikazy[args.POLOHA] || 'antennas.up');
    }

    wave (args) {
        return this._prikaz('antennas.wave', {times: this._num(args.KRAT, 3)});
    }

    wiggle (args) {
        return this._prikaz('antennas.wiggle', {times: this._num(args.KRAT, 4)});
    }

    antennasMove (args) {
        return this._prikaz('antennas.move', {
            right: this._num(args.VPRAVO, 0),
            left: this._num(args.VLAVO, 0)
        });
    }

    _pressed (ktora) {
        // Tlačidlový režim vypína anténam motory, tak sa zapína až tu (plán §11.7).
        connection.watchAntennas();
        const anteny = this._sensors().anteny || {};
        if (ktora === 'left') return !!anteny.left;
        if (ktora === 'right') return !!anteny.right;
        return !!(anteny.left || anteny.right);
    }

    isPressed (args) {
        return this._pressed(args.KTORA);
    }

    waitForPress () {
        connection.watchAntennas();
        return this._waitFor(() => this._pressed('any'));
    }

    whenPressed (args) {
        return this._pressed(args.KTORA);
    }

    // -- hlas ----------------------------------------------------------------

    say (args) {
        return this._prikaz('voice.say', {text: String(args.TEXT)});
    }

    sayInLanguage (args) {
        return this._prikaz('voice.say', {text: String(args.TEXT), lang: args.JAZYK});
    }

    playSound (args) {
        return this._prikaz('voice.play', {sound: args.ZVUK});
    }

    setVolume (args) {
        return this._prikaz('voice.volume', {value: this._num(args.HLASITOST, 60)});
    }

    // -- emócie a tanec ------------------------------------------------------

    emotion (args) {
        return this._prikaz('robot.emotion', {name: args.EMOCIA});
    }

    dance (args) {
        return this._prikaz('robot.dance', {
            seconds: this._num(args.SEKUND, 15),
            bpm: this._num(args.TEMPO, 100)
        });
    }

    danceSong (args) {
        return this._prikaz('robot.dance_song', {song: String(args.SUBOR)});
    }

    // -- oči -----------------------------------------------------------------

    seesFace () {
        return !!(this._sensors().tvar || {}).vidi;
    }

    countFaces () {
        return (this._sensors().tvar || {}).pocet || 0;
    }

    facePosition (args) {
        const tvar = this._sensors().tvar || {};
        return tvar[args.SURADNICA] || 0;
    }

    _color (nazov) {
        // Bridge sleduje len farby, o ktoré blok požiadal - inak zbytočná záťaž.
        connection.watchColor(nazov);
        return (this._sensors().farby || {})[nazov] || {vidi: false, x: 0, y: 0, size: 0};
    }

    seesColor (args) {
        return !!this._color(args.FARBA).vidi;
    }

    colorPosition (args) {
        return this._color(args.FARBA)[args.SURADNICA] || 0;
    }

    brightness () {
        const jas = this._sensors().jas;
        return typeof jas === 'number' ? jas : 0.5;
    }

    isDark () {
        return this.brightness() < 0.25;
    }

    takePhoto (args) {
        return this._prikaz('eyes.take_photo', {filename: String(args.SUBOR)});
    }

    whenSeesFace () {
        return this.seesFace();
    }

    // -- uši -----------------------------------------------------------------

    loudness () {
        return this._sensors().hlasitost || 0;
    }

    isLoud () {
        return this.loudness() > 0.1;
    }

    hearsClap () {
        return connection.clapped();
    }

    waitForClap () {
        const odkedy = connection.clapCount();
        return this._waitFor(() => connection.clapCount() !== odkedy);
    }

    listen () {
        // Jediný reportér, ktorý sa vykonáva v bridgei - má tam strop 15 s.
        return connection.send('ears.listen', {seconds: 0})
            .then(odpoved => (odpoved && odpoved.hodnota) || '');
    }

    record (args) {
        return this._prikaz('ears.record', {
            filename: String(args.SUBOR),
            seconds: this._num(args.SEKUND, 5)
        });
    }

    whenClap () {
        return connection.clapped();
    }

    // -- základ --------------------------------------------------------------

    isRobotConnected () {
        return connection.isRobotReady();
    }

    reset () {
        return this._prikaz('robot.reset');
    }

    sleep () {
        return this._prikaz('robot.sleep');
    }
}

export default Scratch3ReachyBlocks;
